// src/components/layout/MobileMenu.jsx
import React, { useRef, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useUser } from "../../contexts/UserContext";
import { useCart } from "../../contexts/CartContext";
import { useLessons } from "../../contexts/LessonsContext";
import { useMessages } from "../../contexts/MessagesContext";
import { useDocuments } from "../../contexts/DocumentsContext";
import { useTeachersApproval } from "../../contexts/TeachersApprovalContext";
import Badge from "../ui/Badge";
import SpinnerOverlay from "../ui/SpinnerOverlay";

import {
  HelpIcon,
  HomeIcon,
  InfoIcon,
  LoginIcon,
  MailIcon,
  LogoutIcon,
  CourseIcon,
  CartIcon,
  ProfileIcon,
  ClassIcon,
  RequestIcon,
  ReportIcon,
  DocIcon,
  ApproveIcon,
  MessageIcon,
  SearchIcon,
} from "../common/IconsComponent";

const MobileMenu = ({ setMobileMenuOpen, burgerRef }) => {
  const { role, logout, isLoading } = useUser();

  const { lessons: cartLessons } = useCart();
  const totalCount = cartLessons.length;

  const { counts: msgCounts } = useMessages();
  const { adminCounts, loadAdminCounts } = useDocuments();
  const { counts: teachersCounts, loadTeachersCounts } = useTeachersApproval();
  const { pendingPrivateCount } = useLessons();

  const isLoggedIn = role !== "default";
  const menuRef = useRef(null);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  useEffect(() => {
    function handleClickOutside(event) {
      if (burgerRef?.current?.contains(event.target)) return;

      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMobileMenuOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [setMobileMenuOpen, burgerRef]);

  useEffect(() => {
    if (role === "admin") {
      loadAdminCounts?.();
    }
  }, [role, loadAdminCounts]);

  useEffect(() => {
    if (role === "admin") {
      loadTeachersCounts?.();
    }
  }, [role, loadTeachersCounts]);

  const handleLogout = async () => {
    if (isLoggingOut || isLoading) return;

    setIsLoggingOut(true);
    try {
      await logout();
    } finally {
      setIsLoggingOut(false);
      setMobileMenuOpen(false);
    }
  };

  // Kept in the exact same order as Header.js's ROLE_LINKS for every
  // role, so the burger menu never drifts out of sync with the
  // desktop nav again. Icons for items that have no dedicated one in
  // IconsComponent (RedeemCoupon, MyWallet, GpnBudgetPurchases, and
  // every supplier/school item) reuse the closest existing icon below
  // rather than importing an unverified icon name.
  const roleLinks = {
    admin: [
      { to: "/", label: "דף הבית", icon: <HomeIcon className="w-5 h-5 text-indigo-600" /> },
      { to: "/BankTransfersReport", label: "העברות בנקאיות", icon: <CartIcon className="w-5 h-5 text-indigo-600" /> },
      { to: "/TchsIncomeReport", label: "הכנסות מורים", icon: <ReportIcon className="w-5 h-5 text-indigo-600" /> },
      { to: "/TchsTaxReport", label: "תשלומי מס", icon: <InfoIcon className="w-5 h-5 text-indigo-600" /> },
      { to: "/ConfirmDocs", label: "מסמכים", icon: <DocIcon className="w-5 h-5 text-indigo-600" /> },
      { to: "/ConfirmTchrs", label: "אישור מורים", icon: <ApproveIcon className="w-5 h-5 text-indigo-600" /> },
      { to: "/GpnBudgetPurchases", label: "רכישות תקציב גפ״ן", icon: <ReportIcon className="w-5 h-5 text-indigo-600" /> },
      { to: "/Profile", label: "פרופיל", icon: <ProfileIcon className="w-5 h-5 text-indigo-600" /> },
      { to: "/Messages", label: "הודעות", icon: <MessageIcon className="w-5 h-5 text-indigo-600" /> },
      { to: "/SubPhotos", label: "תמונות קורסים", icon: <CourseIcon className="w-5 h-5 text-indigo-600" /> },
    ],

    teacher: [
      { to: "/", label: "דף הבית", icon: <HomeIcon className="w-5 h-5 text-indigo-600" /> },
      { to: "/MyCourses", label: "הקורסים שלי", icon: <CourseIcon className="w-5 h-5 text-indigo-600" /> },
      { to: "/OnlineClass", label: "כיתה מקוונת", icon: <ClassIcon className="w-5 h-5 text-indigo-600" /> },
      { to: "/PrivateLessonsRequests", label: "בקשות שיעור", icon: <RequestIcon className="w-5 h-5 text-indigo-600" /> },
      { to: "/DoneLessons", label: "דוח הכנסות", icon: <ReportIcon className="w-5 h-5 text-indigo-600" /> },
      { to: "/Profile", label: "פרופיל", icon: <ProfileIcon className="w-5 h-5 text-indigo-600" /> },
      { to: "/Contact", label: "צור קשר", icon: <MailIcon className="w-5 h-5 text-indigo-600" /> },
      { to: "/Help", label: "עזרה", icon: <HelpIcon className="w-5 h-5 text-indigo-600" /> },
    ],

    student: [
      { to: "/", label: "דף הבית", icon: <HomeIcon className="w-5 h-5 text-indigo-600" /> },
      { to: "/MyCourses", label: "הקורסים שלי", icon: <CourseIcon className="w-5 h-5 text-indigo-600" /> },
      { to: "/OnlineClass", label: "כיתה מקוונת", icon: <ClassIcon className="w-5 h-5 text-indigo-600" /> },
      { to: "/RedeemCoupon", label: "מימוש קופון גפ״ן", icon: <DocIcon className="w-5 h-5 text-indigo-600" /> },
      { to: "/MyWallet", label: "הארנק שלי", icon: <ReportIcon className="w-5 h-5 text-indigo-600" /> },
      { to: "/Profile", label: "פרופיל", icon: <ProfileIcon className="w-5 h-5 text-indigo-600" /> },
      { to: "/Contact", label: "צור קשר", icon: <MailIcon className="w-5 h-5 text-indigo-600" /> },
      { to: "/Cart", label: "סל שיעורים", icon: <CartIcon className="w-5 h-5 text-indigo-600" /> },
      { to: "/Help", label: "עזרה", icon: <HelpIcon className="w-5 h-5 text-indigo-600" /> },
      { to: "/Search", label: "חיפוש", icon: <SearchIcon className="w-5 h-5 text-indigo-600" /> },
    ],

    // Learning-center / supplier role — same trimmed set as Header.js's
    // ROLE_LINKS.supplier (Students/Schools/Coupons/Refunds/Reports/
    // Settings live inside the Supplier Area itself).
    supplier: [
      { to: "/", label: "דף הבית", icon: <HomeIcon className="w-5 h-5 text-indigo-600" /> },
      { to: "/supplier/dashboard", label: "לוח בקרה", icon: <ReportIcon className="w-5 h-5 text-indigo-600" /> },
      { to: "/supplier/courses", label: "קורסים", icon: <CourseIcon className="w-5 h-5 text-indigo-600" /> },
      { to: "/supplier/teachers", label: "מורים", icon: <ProfileIcon className="w-5 h-5 text-indigo-600" /> },
      { to: "/OnlineClass", label: "שיעורים / מפגשים", icon: <ClassIcon className="w-5 h-5 text-indigo-600" /> },
      { to: "/supplier/programs", label: "תוכניות גפ״ן", icon: <DocIcon className="w-5 h-5 text-indigo-600" /> },
      { to: "/supplier/revenue", label: "הכנסות", icon: <ReportIcon className="w-5 h-5 text-indigo-600" /> },
      { to: "/supplier/revenue/teacher", label: "דוח לפי מורה", icon: <ReportIcon className="w-5 h-5 text-indigo-600" /> },
      { to: "/Profile", label: "פרופיל", icon: <ProfileIcon className="w-5 h-5 text-indigo-600" /> },
      { to: "/Contact", label: "צור קשר", icon: <MailIcon className="w-5 h-5 text-indigo-600" /> },
    ],

    // School-administrator role — same set as Header.js's
    // ROLE_LINKS.school (coupon issuance/budget purchase/rosters live
    // inside the programs page itself).
    school: [
      { to: "/", label: "דף הבית", icon: <HomeIcon className="w-5 h-5 text-indigo-600" /> },
      { to: "/school/dashboard", label: "לוח בקרה", icon: <ReportIcon className="w-5 h-5 text-indigo-600" /> },
      { to: "/school/programs", label: "תוכניות גפ״ן", icon: <DocIcon className="w-5 h-5 text-indigo-600" /> },
      { to: "/Profile", label: "פרופיל", icon: <ProfileIcon className="w-5 h-5 text-indigo-600" /> },
      { to: "/Contact", label: "צור קשר", icon: <MailIcon className="w-5 h-5 text-indigo-600" /> },
      { to: "/Help", label: "עזרה", icon: <HelpIcon className="w-5 h-5 text-indigo-600" /> },
    ],

    default: [
      { to: "/", label: "דף הבית", icon: <HomeIcon className="w-5 h-5 text-indigo-600" /> },
      { to: "/About", label: "אודות", icon: <InfoIcon className="w-5 h-5 text-indigo-600" /> },
      { to: "/Contact", label: "צור קשר", icon: <MailIcon className="w-5 h-5 text-indigo-600" /> },
      { to: "/Help", label: "עזרה", icon: <HelpIcon className="w-5 h-5 text-indigo-600" /> },
      { to: "/Search", label: "חיפוש", icon: <SearchIcon className="w-5 h-5 text-indigo-600" /> },
    ],
  };

  const getCountForItem = (item) => {
    if (role === "student" && item.to === "/Cart") {
      return totalCount;
    }

    if (role === "admin" && item.to === "/Messages") {
      return Number(msgCounts?.globalUnread || 0);
    }

    if (role === "admin" && item.to === "/ConfirmDocs") {
      return Number(adminCounts?.NO_RESPONSE || 0);
    }

    if (role === "admin" && item.to === "/ConfirmTchrs") {
      return Number(teachersCounts?.PENDING || 0);
    }

    if (role === "teacher" && item.to === "/PrivateLessonsRequests") {
      return Number(pendingPrivateCount || 0);
    }

    return 0;
  };

  const navItem = ({ to, label, icon, onClick, key }) => {
    const count = getCountForItem({ to, label });

    return (
      <Link
        key={key || to}
        to={to}
        onClick={() => {
          setMobileMenuOpen(false);
          if (onClick) onClick();
        }}
        className="flex items-center justify-between py-3 px-4 hover:bg-indigo-50 transition-colors"
      >
        <div className="flex items-center min-w-0">
          <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center mr-2 relative shrink-0">
            {to === "/Cart" && role === "student" ? (
              <Badge count={totalCount}>
                <CartIcon className="w-5 h-5 text-indigo-600" />
              </Badge>
            ) : (
              icon
            )}
          </div>

          <span className="font-medium text-sm mr-2">{label}</span>
        </div>

        {count > 0 && to !== "/Cart" && (
          <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-red-600 text-white text-[11px] font-bold flex items-center justify-center">
            {count > 99 ? "99+" : count}
          </span>
        )}
      </Link>
    );
  };

  return (
    <>
      {(isLoggingOut || isLoading) && (
        <SpinnerOverlay
          variant="glow"
          text="נא להמתין"
          dark={false}
          blur={false}
        />
      )}

      <div className="md:hidden absolute right-0 top-11 z-50 w-64" ref={menuRef}>
        <div className="bg-white shadow-xl border border-gray-100 rounded-lg overflow-hidden">
          <nav className="flex flex-col divide-y divide-gray-100 max-h-[75vh] overflow-y-auto">
            {roleLinks[role]?.map((item) => navItem({ ...item, key: item.to }))}

            {isLoggedIn ? (
              <button
                onClick={handleLogout}
                disabled={isLoggingOut || isLoading}
                className={`flex items-center px-4 py-3 transition-colors text-right w-full ${
                  isLoggingOut || isLoading
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-red-50 text-red-600 hover:bg-red-100"
                }`}
              >
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center mr-2 ${
                    isLoggingOut || isLoading ? "bg-gray-200" : "bg-red-100"
                  }`}
                >
                  <LogoutIcon className={isLoggingOut || isLoading ? "text-gray-400" : "text-red-600"} />
                </div>
                <span className="font-medium text-sm mr-2">התנתקות</span>
              </button>
            ) : (
              navItem({
                to: "/login_page",
                label: "התחברות",
                icon: <LoginIcon />,
              })
            )}
          </nav>
        </div>
      </div>
    </>
  );
};

export default MobileMenu;